import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Logo from '@/components/Logo';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import CandidateRegistrationForm from '@/components/candidate/CandidateRegistrationForm';
import CandidateAssessmentOptions from '@/components/candidate/CandidateAssessmentOptions';
import { decryptCandidateDetails } from '@/utils/encryption';
import { publicSupabase, serviceSupabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { getCreditsBalance } from '@/services/creditService';

const CandidateLanding: React.FC = () => {
  const { id: assessmentId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const candidateId = searchParams.get('candidateId');
  const assessmentCompleted = searchParams.get('completed') === 'true';
  const email = searchParams.get('email');
  
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [candidate, setCandidate] = useState<{
    id?: string;
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [assessmentDetails, setAssessmentDetails] = useState<{
    title: string;
    skills: string[];
    company_name: string;
    custom_questions: string[];
  }>({
    title: '',
    skills: [],
    company_name: '',
    custom_questions: []
  });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgCredits, setOrgCredits] = useState<number | null>(null);
  const [checkingCredits, setCheckingCredits] = useState(false);
  const [creditCheckError, setCreditCheckError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Add a development mode fallback function at the top of the component
  const isDevelopment = () => {
    return process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  };

  useEffect(() => {
    const fetchCandidateDetails = async () => {
      if (!assessmentId) {
        toast.error('Invalid assessment ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // If assessment is completed and we have an email, set it directly
        if (assessmentCompleted && email) {
          setCandidate(prev => ({ ...prev, email }));
          setLoading(false);
          return;
        }

        // Check if the candidate has already completed this assessment
        if (email) {
          const { data: existingCandidates, error: checkError } = await publicSupabase
            .from('candidates')
            .select('id, status')
            .eq('assessment_id', assessmentId)
            .eq('email', email);

          if (!checkError && existingCandidates && existingCandidates.length > 0) {
            const existingCandidate = existingCandidates[0];
            if (existingCandidate.status === 'completed') {
              toast.error(
                'You have already completed this assessment. Each candidate can only take an assessment once.',
                {
                  duration: 5000,
                  description: 'If you believe this is a mistake, please contact the assessment administrator.'
                }
              );
              throw new Error('Assessment already completed');
            }
          }
        }

        // First verify the assessment exists and is active using a more permissive query
        const { data: assessments, error: assessmentError } = await publicSupabase
          .from('assessments')
          .select(`
            id, 
            status, 
            title,
            skills,
            organization_id,
            custom_questions
          `)
          .eq('id', assessmentId);

        //console.log('[CANDIDATE LANDING] Assessment query result:', 
        //  { 
        //    assessments, 
        //    error: assessmentError,
        //    timestamp: new Date().toISOString()
        //  });

        if (assessmentError) {
          // console.error('[CANDIDATE LANDING] Assessment fetch error:', {
          //   error: assessmentError,
          //   message: assessmentError.message,
          //   details: assessmentError.details,
          //   hint: assessmentError.hint,
          //   timestamp: new Date().toISOString()
          // });
          throw new Error(`Failed to verify assessment: ${assessmentError.message}`);
        }

        if (!assessments || assessments.length === 0) {
          // console.error('[CANDIDATE LANDING] Assessment not found:', {
          //   assessmentId,
          //   queryResult: assessments,
          //   timestamp: new Date().toISOString()
          // });
          throw new Error('Assessment not found');
        }

        const assessment = assessments[0];
        //console.log('[CANDIDATE LANDING] Assessment found:', assessment);

        if (assessment.status !== 'active') {
          // console.error('[CANDIDATE LANDING] Assessment not active:', {
          //   status: assessment.status,
          //   timestamp: new Date().toISOString()
          // });
          throw new Error('This assessment is not currently active');
        }

        // Set assessment details from the database
        let organizationName = 'Unknown Organization';
        
        // Try to get organization name if organization_id is available
        if (assessment.organization_id) {
          // console.log('[CANDIDATE LANDING] Fetching organization details:', {
          //   organizationId: assessment.organization_id,
          //   timestamp: new Date().toISOString()
          // });
          
          setOrganizationId(assessment.organization_id);
          
          try {
            const { data: organization, error: orgError } = await publicSupabase
              .from('organizations')
              .select('name')
              .eq('id', assessment.organization_id)
              .single();
              
            if (!orgError && organization && organization.name) {
              organizationName = organization.name;
              // console.log('[CANDIDATE LANDING] Found organization name:', {
              //   name: organizationName,
              //   timestamp: new Date().toISOString()
              // });
            } else {
              // console.warn('[CANDIDATE LANDING] Error fetching organization:', {
              //   error: orgError,
              //   organizationId: assessment.organization_id,
              //   timestamp: new Date().toISOString()
              // });
            }
            
            // Check organization credits
            try {
              setCheckingCredits(true);
              let credits = 0;
              
              try {
                credits = await getCreditsBalance(assessment.organization_id);
                // console.log('[CANDIDATE LANDING] Organization credits:', credits);
              } catch (creditError: any) {
                // console.error('[CANDIDATE LANDING] Error checking organization credits:', {
                //   error: creditError,
                //   message: creditError?.message,
                //   details: creditError?.details,
                //   code: creditError?.code
                // });
                
                // If it's a specific Supabase error about missing data, treat as 0 credits
                if (creditError?.code === 'PGRST116' || 
                    (creditError?.message && creditError.message.includes('multiple (or no) rows returned'))) {
                  // console.log('[CANDIDATE LANDING] No credits record found, assuming 0 credits');
                  credits = 0;
                } else {
                  // For other errors, set error state but don't block the form
                  setCreditCheckError('Unable to verify assessment availability');
                }
              }
              
              // Development mode fallback - only in local/development environments
              if (credits <= 0 && isDevelopment()) {
                // console.log('[CANDIDATE LANDING] Development environment detected - using fallback credits of 10');
                credits = 10; // Development fallback
                setCreditCheckError(null);
              }
              
              setOrgCredits(credits);

              // After initial credit check result shows 0 credits and we're not in development mode
              if (credits === 0 && !isDevelopment()) {
                // console.log('[CANDIDATE LANDING] Initial credits are 0, trying direct query with service role...');
                try {
                  // First check credits table directly with service role
                  const { data: directCreditsData, error: directCreditsError } = await serviceSupabase
                    .from('credits')
                    .select('remaining_credits')
                    .eq('organization_id', assessment.organization_id)
                    .maybeSingle();
                    
                  // console.log('[CANDIDATE LANDING] Direct credits table query with service role:', { directCreditsData, directCreditsError });
                  
                  if (!directCreditsError && directCreditsData && directCreditsData.remaining_credits > 0) {
                    //console.log('[CANDIDATE LANDING] Found credits directly in credits table with service role:', directCreditsData.remaining_credits);
                    setOrgCredits(directCreditsData.remaining_credits);
                    setCreditCheckError(null);
                  }
                  
                  // If that fails, try organization_credit_history table directly with service role
                  const { data: directHistoryData, error: directHistoryError } = await serviceSupabase
                    .from('organization_credit_history')
                    .select('balance')
                    .eq('organization_id', assessment.organization_id)
                    .order('created_at', { ascending: false })
                    .limit(1);
                    
                  // console.log('[CANDIDATE LANDING] Direct history table query with service role:', { directHistoryData, directHistoryError });
                  
                  if (!directHistoryError && directHistoryData && directHistoryData.length > 0 && directHistoryData[0].balance > 0) {
                    // console.log('[CANDIDATE LANDING] Found credits directly in history table with service role:', directHistoryData[0].balance);
                    setOrgCredits(directHistoryData[0].balance);
                    setCreditCheckError(null);
                  }
                } catch (directError) {
                  // console.error('[CANDIDATE LANDING] Error with direct service role queries:', directError);
                }
              }
            } catch (e) {
              // console.error('[CANDIDATE LANDING] Unexpected error checking credits:', e);
            } finally {
              setCheckingCredits(false);
            }
          } catch (orgFetchError) {
            // console.error('[CANDIDATE LANDING] Exception fetching organization:', {
            //   error: orgFetchError,
            //   timestamp: new Date().toISOString()
            // });
          }
        }
        
        //console.log('[CANDIDATE LANDING] Setting assessment details from database:', {
        //  title: assessment.title,
        //  skills: assessment.skills || [],
        //  custom_questions: assessment.custom_questions || [],
        //  organizationName,
        //  timestamp: new Date().toISOString()
        //});

        setAssessmentDetails({
          title: assessment.title || '',
          skills: assessment.skills || [],
          company_name: organizationName,
          custom_questions: assessment.custom_questions || []
        });

        // Case 1: CandidateId provided (pre-registered candidate)
        if (candidateId) {
          const { data: candidates, error: candidateError } = await publicSupabase
            .from('candidates')
            .select('id, name, email, phone')
            .eq('assessment_id', assessmentId)
            .eq('id', candidateId);

          if (candidateError) {
            // console.error('[CANDIDATE LANDING] Candidate fetch error:', {
            //   error: candidateError,
            //   timestamp: new Date().toISOString()
            // });
            throw new Error(`Failed to fetch candidate details: ${candidateError.message}`);
          }
          
          if (candidates && candidates.length > 0) {
            const data = candidates[0];
            setCandidate({
              id: data.id,
              name: data.name,
              email: data.email,
              phone: data.phone || undefined
            });
            setRegistrationComplete(!!data.name);
          }
        }
        // Case 2: Token provided (from copied link)
        else if (token) {
          const decryptedDetails = decryptCandidateDetails(token);
          // console.log('[CANDIDATE LANDING] Decrypted token details:', decryptedDetails);
          
          if (!decryptedDetails) {
            throw new Error('Invalid registration token');
          }

          if (decryptedDetails.type === 'registration' && decryptedDetails.assessmentId === assessmentId) {
            // For registration tokens, we start with an empty form
            setCandidate({});
            setRegistrationComplete(false);
            
            // Only set assessment details from token if they exist and have values
            if (decryptedDetails.assessmentDetails && 
                decryptedDetails.assessmentDetails.organization && 
                decryptedDetails.assessmentDetails.title) {
              
              // console.log('[CANDIDATE LANDING] Setting assessment details from token:', {
              //   details: decryptedDetails.assessmentDetails,
              //   organization: decryptedDetails.assessmentDetails.organization,
              //   timestamp: new Date().toISOString()
              // });
              
              // Make sure to use the organization field as company_name
              setAssessmentDetails(prevDetails => ({
                ...prevDetails, // Keep existing database-fetched details as fallback
                title: decryptedDetails.assessmentDetails?.title || prevDetails.title,
                skills: decryptedDetails.assessmentDetails?.skills || prevDetails.skills,
                company_name: decryptedDetails.assessmentDetails?.organization || prevDetails.company_name
              }));
            } else {
              // console.warn('[CANDIDATE LANDING] No or incomplete assessment details in token, using database values');
              // We already set assessment details from database above
            }
          } else if (decryptedDetails.type === 'invite' && decryptedDetails.assessmentId === assessmentId) {
            // For invite tokens
            setCandidate({
              name: decryptedDetails.name,
              email: decryptedDetails.email,
              id: decryptedDetails.candidateId // Extract candidateId from token if present
            });
            setRegistrationComplete(true);
          } else {
            throw new Error('Invalid token type or assessment mismatch');
          }
        } else {
          throw new Error('No valid registration method provided');
        }
      } catch (error: any) {
        // console.error('[CANDIDATE LANDING] Error fetching candidate details:', {
        //   error,
        //   timestamp: new Date().toISOString()
        // });
        toast.error(error.message || 'Failed to load candidate details');
        // Redirect to error page or show error state
        setCandidate({});
        setRegistrationComplete(false);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateDetails();
  }, [assessmentId, candidateId, token]);

  const handleRegistrationComplete = async (candidateDetails: {
    id: string;
    name: string;
    email: string;
    phone?: string;
  }) => {
    // Check if the candidate has already completed this assessment
    const { data: candidates, error: checkError } = await publicSupabase
      .from('candidates')
      .select('id, status')
      .eq('assessment_id', assessmentId)
      .eq('email', candidateDetails.email);

    if (!checkError && candidates && candidates.length > 0) {
      const existingCandidate = candidates[0];
      if (existingCandidate.status === 'completed') {
        toast.error(
          'You have already completed this assessment. Each candidate can only take an assessment once.',
          {
            duration: 5000,
            description: 'If you believe this is a mistake, please contact the assessment administrator.'
          }
        );
        return;
      }
    }

    // console.log('[CANDIDATE LANDING] Registration complete, showing assessment options:', candidateDetails);
    setCandidate(candidateDetails);
    setRegistrationComplete(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleContinue = async () => {
    if (!formData.fullName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check if organization has enough credits before proceeding
    if (organizationId) {
      try {
        setCheckingCredits(true);
        let credits = 0;
        
        try {
          // First try the standard getCreditsBalance function
          credits = await getCreditsBalance(organizationId);
          // console.log('[CANDIDATE LANDING] Organization credits for registration:', credits);
        } catch (creditError: any) {
          // console.error('[CANDIDATE LANDING] Error checking credits for registration:', {
          //   error: creditError,
          //   message: creditError?.message,
          //   details: creditError?.details,
          //   code: creditError?.code
          // });
          
          // Fallback to direct query with service role
          try {
            const { data: directData, error: directError } = await serviceSupabase
              .from('credits')
              .select('remaining_credits')
              .eq('organization_id', organizationId)
              .maybeSingle();
              
            if (!directError && directData && directData.remaining_credits > 0) {
              // console.log('[CANDIDATE LANDING] Found credits with service role in registration:', directData.remaining_credits);
              credits = directData.remaining_credits;
            }
          } catch (serviceError) {
            // console.error('[CANDIDATE LANDING] Service role fallback also failed:', serviceError);
          }
        }
        
        // Development mode fallback - only in local/development environments
        if (credits <= 0 && isDevelopment()) {
          // console.log('[CANDIDATE LANDING] Development environment for registration - using fallback credits of 10');
          credits = 10; // Development fallback
          setCreditCheckError(null);
        }
        
        setOrgCredits(credits);
        
        if (credits <= 0 && !isDevelopment()) {
          // console.log('[CANDIDATE LANDING] Organization has insufficient credits:', credits);
          setCreditCheckError('This assessment is currently paused. Please contact the hiring manager for assistance.');
          setCheckingCredits(false);
          return;
        }
        setCheckingCredits(false);
      } catch (e) {
        // console.error('[CANDIDATE LANDING] Unexpected error in registration credit check:', e);
        
        // Development fallback in case of unexpected errors
        if (isDevelopment()) {
          // console.log('[CANDIDATE LANDING] Development environment after registration error - using fallback credits of 10');
          setOrgCredits(10);
          setCreditCheckError(null);
        }
        
        setCheckingCredits(false);
      }
    }

    setIsSubmitting(true);
    
    try {
      // First, check if the candidate with the same email already exists for this assessment
      const { data: existingCandidates, error: checkError } = await publicSupabase
        .from('candidates')
        .select('id, status')
        .eq('assessment_id', assessmentId)
        .eq('email', formData.email);
      
      if (checkError) {
        // console.error('[CANDIDATE LANDING] Error checking existing candidate:', checkError);
        throw checkError;
      }
      
      // If candidate already exists, handle accordingly
      if (existingCandidates && existingCandidates.length > 0) {
        // console.log('[CANDIDATE LANDING] Candidate already exists:', existingCandidates[0]);
        
        // Get the existing candidate details
        const { data: candidateData, error: candidateError } = await publicSupabase
          .from('candidates')
          .select('id, name, email, phone')
          .eq('id', existingCandidates[0].id)
          .single();
          
        if (candidateError) {
          // console.error('[CANDIDATE LANDING] Error fetching existing candidate details:', candidateError);
          throw candidateError;
        }
        
        if (candidateData) {
          // console.log('[CANDIDATE LANDING] Using existing candidate record:', candidateData);
          // Use the existing candidate data
          handleRegistrationComplete({
            id: candidateData.id,
            name: candidateData.name,
            email: candidateData.email,
            phone: candidateData.phone
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      // Insert new candidate record
      // console.log('[CANDIDATE LANDING] Registering new candidate:', {
      //   assessment_id: assessmentId,
      //   name: formData.fullName,
      //   email: formData.email,
      //   phone: formData.phone,
      //   timestamp: new Date().toISOString()
      // });
      
      const { data, error } = await publicSupabase
        .from('candidates')
        .insert({
          assessment_id: assessmentId,
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone || null,
          status: 'in_progress',
          source: 'RoundOne'
        })
        .select('id, name, email, phone')
        .single();
      
      if (error) {
        // console.error('[CANDIDATE LANDING] Error inserting candidate:', error);
        
        // Handle specific insert errors
        if (error.code === '23505') {
          // console.log('[CANDIDATE LANDING] Conflict error detected, handling specially:', error);
          
          // Get the existing candidate by email to handle the conflict
          const { data: conflict, error: conflictError } = await publicSupabase
            .from('candidates')
            .select('id, name, email, phone')
            .eq('assessment_id', assessmentId)
            .eq('email', formData.email)
            .single();
            
          if (conflictError) {
            // console.error('[CANDIDATE LANDING] Error finding conflicting candidate:', conflictError);
            toast.error('Registration failed. Please try again with a different email address.');
            setIsSubmitting(false);
            return;
          }
          
          if (conflict) {
            // console.log('[CANDIDATE LANDING] Found conflicting candidate, using existing record:', conflict);
            handleRegistrationComplete({
              id: conflict.id,
              name: conflict.name,
              email: conflict.email,
              phone: conflict.phone
            });
            setIsSubmitting(false);
            return;
          } else {
            // console.warn('[CANDIDATE LANDING] Conflict reported but no record found, showing error');
            toast.error('A candidate with this email already exists for this assessment. Please use a different email address.');
            setIsSubmitting(false);
            return;
          }
        } else if (error.code === '23596') {
          toast.error('Registration failed. Please try again with a different email address.');
          setIsSubmitting(false);
          return;
        } else if (error.code === 'PGRST116' || error.message?.includes('multiple') || error.message?.includes('rows returned')) {
          // console.log('[CANDIDATE LANDING] Conflict error detected, showing error message:', error);
          toast.error('A candidate with this email already exists for this assessment. Please use a different email address.');
          setIsSubmitting(false);
          return;
        }
        
        throw error;
      }

      if (data) {
        // console.log('[CANDIDATE LANDING] Successfully registered candidate, transitioning to assessment options:', data);
        handleRegistrationComplete(data);
      }
    } catch (error: any) {
      // console.error('[CANDIDATE LANDING] Error registering candidate:', error);
      
      // Show more specific error message based on error details
      if (error?.code === '23505') {
        toast.error('A candidate with this email already exists for this assessment. Please use a different email address.');
      } else if (error?.code === '23596') {
        toast.error('Registration failed. Please try again with a different email address.');
      } else if (error?.code === 'PGRST116' || error?.message?.includes('multiple') || error?.message?.includes('rows returned')) {
        toast.error('A candidate with this email already exists for this assessment. Please use a different email address.');
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to register. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  // Add a helper function for credit check retries
  const retryCreditsCheck = async (orgId: string, attempt: number) => {
    // console.log(`[CANDIDATE LANDING] Retry credit check attempt ${attempt} for org ${orgId}`);
    try {
      // Try the service function first
      const retryCredits = await getCreditsBalance(orgId);
      // console.log(`[CANDIDATE LANDING] Retry ${attempt} credit check result:`, retryCredits);
      
      if (retryCredits > 0) {
        setOrgCredits(retryCredits);
        setCreditCheckError(null);
        return;
      }
      
      // If that returns 0, try direct queries with service role client
      const { data: creditsData } = await serviceSupabase
        .from('credits')
        .select('remaining_credits')
        .eq('organization_id', orgId)
        .maybeSingle();
        
      if (creditsData && creditsData.remaining_credits > 0) {
        // console.log(`[CANDIDATE LANDING] Retry ${attempt} found credits in credits table with service role:`, creditsData.remaining_credits);
        setOrgCredits(creditsData.remaining_credits);
        setCreditCheckError(null);
        return;
      }
      
      // Try history table with service role client
      const { data: historyData } = await serviceSupabase
        .from('organization_credit_history')
        .select('balance')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (historyData && historyData.length > 0 && historyData[0].balance > 0) {
        // console.log(`[CANDIDATE LANDING] Retry ${attempt} found credits in history table with service role:`, historyData[0].balance);
        setOrgCredits(historyData[0].balance);
        setCreditCheckError(null);
        return;
      }
      
      // For development, provide a fallback on the final retry attempt
      if (isDevelopment() && attempt === 3) {
        // console.log(`[CANDIDATE LANDING] Final retry ${attempt} in development - using fallback of 10 credits`);
        setOrgCredits(10);
        setCreditCheckError(null);
      }
    } catch (error) {
      // console.error(`[CANDIDATE LANDING] Error in retry ${attempt}:`, error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-[#F8F7FF] flex flex-col">
      {/* Header */}
      <header className="w-full border-b bg-white/80 backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center px-4">
          <Logo size="md" />
        </div>
      </header>
      
      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-roundone-purple"></div>
            </div>
          ) : (
            <>
              {!registrationComplete ? (
                <Card className="glass-card border border-primary/10 shadow-md animate-fade-in overflow-hidden">
                  <CardContent className="p-6">
                    <div className="space-y-5">
                      <div className="flex justify-center mb-2">
                        <div className="p-3 rounded-full bg-roundone-light-purple/20">
                          <CheckCircle2 size={28} className="text-roundone-purple" />
                        </div>
                      </div>
                      <h2 className="text-xl font-bold text-center text-roundone-dark-purple mb-1">
                        {assessmentDetails.title || 'Technical Assessment'}
                      </h2>
                      <p className="text-center text-muted-foreground mb-4">
                        {assessmentCompleted
                          ? 'Thank you for completing this assessment.'
                          : 'Please complete your information below to continue.'}
                      </p>

                      {/* No Credits Message - Show if organization has no credits */}
                      {orgCredits !== null && orgCredits <= 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 my-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <AlertCircle className="h-5 w-5 text-red-500" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-red-800">Assessment Unavailable</h3>
                              <div className="mt-1 text-sm text-red-700">
                                <p>This assessment is currently paused. Please contact the hiring manager for assistance.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {checkingCredits ? (
                        <div className="p-4 text-center">
                          <div className="animate-spin h-5 w-5 border-2 border-roundone-purple border-opacity-30 border-t-roundone-purple rounded-full inline-block mr-2"></div>
                          <span className="text-sm text-muted-foreground">Checking assessment availability...</span>
                        </div>
                      ) : (
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          handleContinue();
                        }} className="space-y-5">
                          <div className="space-y-2">
                            <label htmlFor="fullName" className="text-sm font-medium">
                              Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="fullName"
                              type="text"
                              required
                              value={formData.fullName}
                              onChange={handleInputChange}
                              disabled={isSubmitting || (orgCredits !== null && orgCredits <= 0)}
                              className="w-full px-4 py-2.5 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-roundone-purple/30 focus:border-roundone-light-purple transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder="Enter your full name"
                            />
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="email" className="text-sm font-medium">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="email"
                              type="email"
                              required
                              value={formData.email}
                              onChange={handleInputChange}
                              disabled={isSubmitting || (orgCredits !== null && orgCredits <= 0)}
                              className="w-full px-4 py-2.5 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-roundone-purple/30 focus:border-roundone-light-purple transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder="Enter your email address"
                            />
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="phone" className="text-sm font-medium">
                              Phone Number <span className="text-muted-foreground text-sm font-normal">(Optional)</span>
                            </label>
                            <input
                              id="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              disabled={isSubmitting || (orgCredits !== null && orgCredits <= 0)}
                              className="w-full px-4 py-2.5 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-roundone-purple/30 focus:border-roundone-light-purple transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder="Enter your phone number"
                            />
                          </div>

                          <Button
                            type="submit"
                            className="w-full gradient-button py-2.5 mt-2 flex items-center justify-center gap-2 text-white"
                            disabled={isSubmitting || !formData.fullName || !formData.email || (orgCredits !== null && orgCredits <= 0)}
                          >
                            {isSubmitting ? (
                              <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-30 border-t-white rounded-full"></div>
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                <span>Continue</span>
                                <ArrowRight className="h-4 w-4" />
                              </>
                            )}
                          </Button>
                          
                          <div className="text-center text-xs text-muted-foreground mt-6">
                            Powered by <a href="https://roundone.ai" className="text-roundone-purple hover:underline">RoundOne AI</a>
                          </div>
                        </form>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="glass-card border border-primary/10 shadow-md animate-fade-in overflow-hidden">
                  <CardContent className="p-6">
                    <CandidateAssessmentOptions
                      assessmentId={assessmentId!}
                      candidateId={candidate.id!}
                      candidateName={candidate.name!}
                      candidateEmail={candidate.email!}
                      candidatePhone={candidate.phone}
                      assessmentDetails={{
                        title: assessmentDetails.title,
                        skills: assessmentDetails.skills,
                        company_name: assessmentDetails.company_name,
                        custom_questions: assessmentDetails.custom_questions
                      }}
                    />
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default CandidateLanding;
